import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Grid, Box } from "@mui/material";
import PropTypes from "prop-types";

import { postCall, deleteCall_v2, postCall_v2 } from "services";
import Attachment from "./Attachment";
import { fileUploadListData } from "../constants";
import { success, error } from "constants";
import { useDownloadInvoiceFile, useToaster } from "hooks";

const FileUpload = forwardRef(
  ({ fileList, shipmentId, invoiceNumber, shipmentNumber }, ref) => {
    const [fileDetails, setFileDetails] = useState({});

    const triggerToaster = useToaster();

    const { triggerDownloadInvoice, triggerDownloadInvoiceViaJoms } =
      useDownloadInvoiceFile();

    useEffect(() => {
      if (fileList) {
        setFileDetails(fileList);
      }
    }, [fileList]);

    useImperativeHandle(
      ref,
      () => ({
        getUploadedInvoices() {
          return fileDetails?.invoice;
        },
      }),
      [fileDetails],
    );

    const fileUploadAction = (action, msg = null) => {
      if (action === success) {
        triggerToaster(msg || `Files uploaded`, action);
      } else {
        triggerToaster(msg || `Error occurred, please try again`, action);
      }
    };

    const handleUpload = async (files, fileType) => {
      if (invoiceNumber) {
        const formData = new FormData();
        files?.length &&
          Object.keys(files).forEach((file) =>
            formData.append("files", files[file]),
          );
        const resp = await postCall(
          `/oms/shipment/documents/upload`,
          formData,
          {
            shipmentId,
            fileType,
            invoiceNumber,
          },
          (err) => {
            fileUploadAction(error, err?.data?.detail);
          },
        );
        if (resp) {
          let mergedFiles = resp[shipmentId]?.invoiceDetails[0]?.files;
          setFileDetails((prev) => ({
            ...prev,
            ...mergedFiles,
            loading:
              Object.keys(mergedFiles?.loading ?? {}).length > 0
                ? mergedFiles.loading
                : prev.loading,

            unloading:
              Object.keys(mergedFiles?.unloading ?? {}).length > 0
                ? mergedFiles.unloading
                : prev.unloading,
          }));
          fileUploadAction(success);
        }
      } else {
        fileUploadAction(
          error,
          "Please create invoice delivery to upload documents",
        );
      }
    };

    const handleUploadViaJoms = async (files, fileType) => {
      if (invoiceNumber) {
        const formData = new FormData();
        files?.length &&
          Object.keys(files).forEach((file) =>
            formData.append("files", files[file]),
          );
        const { data } = await postCall_v2(
          `/joms/api/internal/v1/shipment/doc`,
          formData,
          {
            shipmentNumber: shipmentNumber,
            type: fileType,
            invoice: invoiceNumber,
          },
          (err) => {
            fileUploadAction(error, err?.data?.detail);
          },
        );
        if (data?.isSuccess) {
          // setFileDetails(resp[shipmentId]?.invoiceDetails[0]?.files);
          //set files
          const uploadedFiles = {};
          data?.uploadedDoc?.map((ele) => (uploadedFiles[ele?.path] = ele));
          const key = fileType.toLowerCase();
          const mergedFiles = { ...fileDetails[key], ...uploadedFiles };
          setFileDetails((prev) => ({
            ...prev,
            [key]: mergedFiles,
          }));

          fileUploadAction(
            success,
            `Uploaded ${data?.uploadedDoc?.length} files successfully and failed uploading ${data?.failedDoc?.length} files`,
          );
        }
      }
    };

    const handleFileDownload = (file, fileType) => {
      triggerDownloadInvoice(file, fileType, shipmentId, invoiceNumber);
    };

    const handleFileDownloadViaJoms = (file, fileType) => {
      triggerDownloadInvoiceViaJoms(
        file,
        fileType,
        shipmentNumber,
        invoiceNumber,
      );
    };

    const handleFileDelete = async (fileType, file, toggleModal) => {
      try {
        await deleteCall_v2(
          "/oms/shipment/documents",
          {},
          {
            fileType: fileType.fileType,
            shipmentId,
            path: file?.path,
          },
        );
        let tempData = structuredClone(fileDetails);
        delete tempData?.[fileType?.key]?.[file?.generatedId];
        setFileDetails(tempData);
        triggerToaster("File is deleted", success);
        Object.keys(tempData?.[fileType?.key]).length || toggleModal();
      } catch (ex) {
        triggerToaster(
          ex?.data?.detail ?? "File not deleted. Please try later",
          error,
        );
        console.error(`Error in deleting document ${ex}`);
      }
    };

    const handleFileDeleteViaJoms = async (fileType, file, toggleModal) => {
      try {
        await deleteCall_v2(
          "/joms/api/internal/v1/shipment/doc",
          {},
          {
            type: fileType.fileType,
            shipmentNumber,
            fileId: file?.path,
          },
        );
        let tempData = structuredClone(fileDetails);
        delete tempData?.[fileType?.key]?.[file?.generatedId];
        setFileDetails(tempData);
        triggerToaster("File is deleted", success);
        Object.keys(tempData?.[fileType?.key]).length || toggleModal();
      } catch (ex) {
        triggerToaster(
          ex?.data?.detail ?? "File not deleted. Please try later",
          error,
        );
        console.error(`Error in deleting document ${ex}`);
      }
    };

    return (
      <Box pt={3} pb={3}>
        <Grid container spacing={3} mb={3}>
          <Grid p={2} xs style={{ border: 1 }}>
            <Attachment
              fileType={fileUploadListData?.invoice}
              fileDetails={fileDetails?.invoice}
              onFileChange={handleUpload}
              onFileDownload={handleFileDownload}
              onFileDelete={handleFileDelete}
            />
          </Grid>
          <Grid p={2} xs style={{ border: 1 }}>
            <Attachment
              fileType={fileUploadListData?.testCertificate}
              fileDetails={fileDetails?.testCertificate}
              onFileChange={handleUpload}
              onFileDownload={handleFileDownload}
              onFileDelete={handleFileDelete}
            />
          </Grid>
          <Grid p={2} xs style={{ border: 1 }}>
            <Attachment
              fileType={fileUploadListData?.weightmentSlips}
              fileDetails={fileDetails?.weightmentSlips}
              onFileChange={handleUpload}
              onFileDownload={handleFileDownload}
              onFileDelete={handleFileDelete}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid p={2} xs={4} style={{ border: 1 }}>
            <Attachment
              fileType={fileUploadListData?.lorryReceipt}
              fileDetails={fileDetails?.lorryReceipt}
              onFileChange={handleUpload}
              onFileDownload={handleFileDownload}
              onFileDelete={handleFileDelete}
            />
          </Grid>
          <Grid p={2} xs={4} style={{ border: 1 }}>
            <Attachment
              fileType={fileUploadListData?.other}
              fileDetails={fileDetails?.other}
              onFileChange={handleUpload}
              onFileDownload={handleFileDownload}
              onFileDelete={handleFileDelete}
            />
          </Grid>
          <Grid p={2} xs={4} style={{ border: 1 }}>
            <Attachment
              fileType={fileUploadListData?.loading}
              fileDetails={fileDetails?.loading}
              onFileChange={handleUploadViaJoms}
              onFileDownload={handleFileDownloadViaJoms}
              onFileDelete={handleFileDeleteViaJoms}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid p={2} xs={4} style={{ border: 1 }}>
            <Attachment
              fileType={fileUploadListData?.unloading}
              fileDetails={fileDetails?.unloading}
              onFileChange={handleUploadViaJoms}
              onFileDownload={handleFileDownloadViaJoms}
              onFileDelete={handleFileDeleteViaJoms}
            />
          </Grid>
        </Grid>
      </Box>
    );
  },
);

FileUpload.propTypes = {
  fileList: PropTypes.object,
  shipmentId: PropTypes.number,
  invoiceNumber: PropTypes.string,
  shipmentNumber: PropTypes.string,
};

export default FileUpload;
